import { atom, selector, DefaultValue } from 'recoil';

export type TourStage = 'loading' | 'startPage' | 'openingStop' | 'map';

export const atom_tourStage = atom<TourStage>({
	key: 'tourStage',
	default: 'loading',
});

export type LoadingPhase =
	| 'fetching_tour'
	| 'syncing_user_data'
	| 'loading_boundaries'
	| 'validating_access'
	| 'preparing_tour';

export interface LoadingProgress {
	phase: LoadingPhase;
	percent: number;
}

// Internal atom that stores the actual progress
const atom_loadingProgressInternal = atom<LoadingProgress>({
	key: 'loadingProgressInternal',
	default: {
		phase: 'fetching_tour',
		percent: 0,
	},
});

// Phase order for comparison (higher index = more advanced)
const PHASE_ORDER: LoadingPhase[] = [
	'fetching_tour',
	'syncing_user_data',
	'loading_boundaries',
	'validating_access',
	'preparing_tour',
];

// Selector that ensures progress only goes UP, never down (except for reset)
export const atom_loadingProgress = selector<LoadingProgress>({
	key: 'loadingProgress',
	get: ({ get }) => get(atom_loadingProgressInternal),
	set: ({ get, set }, newValue) => {
		if (newValue instanceof DefaultValue) {
			set(atom_loadingProgressInternal, newValue);
			return;
		}

		// Allow reset to 0 (start of new loading)
		if (newValue.percent === 0 && newValue.phase === 'fetching_tour') {
			set(atom_loadingProgressInternal, newValue);
			return;
		}

		const currentProgress = get(atom_loadingProgressInternal);
		const currentPhaseIndex = PHASE_ORDER.indexOf(currentProgress.phase);
		const newPhaseIndex = PHASE_ORDER.indexOf(newValue.phase);

		// Only update if:
		// 1. New percent is higher, OR
		// 2. Same percent but more advanced phase
		if (
			newValue.percent > currentProgress.percent ||
			(newValue.percent === currentProgress.percent && newPhaseIndex > currentPhaseIndex)
		) {
			set(atom_loadingProgressInternal, newValue);
		}
	},
});